import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@unite-us/ui';
import { SelectField } from 'common/form/SelectField';
import { SearchableField } from 'common/form/SearchableField';
import { useFeatureFlag } from 'common/hooks';
import { AUTHORIZATION_STATUSES } from 'common/Status/authorizationConstants';
import ContractedProgramFilter from './ContractedProgramFilter';

const FilterSelectors = ({
  filterValues,
  filterOptions,
  resetFilters,
  openFilterDrawer,
  setRequestIdFilter,
  setClientNameFilter,
  setStatusFilter,
  setContractedProgramFilter,
  setPrimaryHealthInsuranceMemberIdFilter,
  authorizationStatus,
}) => {
  const contractedProgramFilterRef = useRef(null);
  const clearFilters = () => {
    resetFilters();

    if (contractedProgramFilterRef.current) {
      contractedProgramFilterRef.current.setSelectedOptions([]);
    }
  };

  const hasFilterDrawer = useFeatureFlag('pays-7500-auth-workqueue-filter-drawer');

  return (
    <div className="flex items-start items-center gap-4 flex-wrap">
      <SearchableField
        className="w-64"
        label="Request ID"
        onChange={({ value }) => {
          setRequestIdFilter(value);
        }}
        searchQuery={filterValues.requestId}
        onInputChange={setRequestIdFilter}
        placeholder="Search by ID"
        clearable
      />
      <SearchableField
        className="w-64"
        label="Client Name"
        onChange={({ value }) => {
          setClientNameFilter(value);
        }}
        searchQuery={filterValues.clientName}
        onInputChange={setClientNameFilter}
        placeholder="Search by client"
        clearable
      />
      {!hasFilterDrawer &&
        authorizationStatus !== AUTHORIZATION_STATUSES.open &&
        authorizationStatus !== AUTHORIZATION_STATUSES.rejected && (
        <SelectField
          className="w-64"
          label="Status"
          placeholder="Choose Status"
          value={filterValues.status ?? ''}
          onChange={({ value }) => {
            setStatusFilter(value);
          }}
          options={filterOptions.status}
          clearable
        />
      )}
      <ContractedProgramFilter
        options={filterOptions.contractedProgram}
        ref={contractedProgramFilterRef}
        selectedValues={filterValues.contractedPrograms}
        setContractedProgramFilter={setContractedProgramFilter}
      />
      {!hasFilterDrawer && (
        <SearchableField
          className="w-64"
          label="Insurance Member ID"
          onChange={({ value }) => {
            setPrimaryHealthInsuranceMemberIdFilter(value);
          }}
          searchQuery={filterValues.primaryHealthInsuranceMemberId}
          onInputChange={setPrimaryHealthInsuranceMemberIdFilter}
          placeholder="Search by Insurance Member ID"
          clearable
        />
      )}
      {!hasFilterDrawer && (
        <Button
          id="clear-all-filters-auths-btn"
          onClick={clearFilters}
          label="Clear all"
          className="mt-5"
          link
        />
      )}
      {hasFilterDrawer && (
        <button
          type="button"
          aria-label="All Filters"
          className="min-w-max pt-8 md:pt-6 lg:pt-6 lg:max-xlg:6 xl:pt-8 "
          onClick={openFilterDrawer}
        >
          <Icon
            ariaLabel="All Filters"
            className="fill-current text-brand-blue"
            icon="IconFilter"
          />
          <span className="ml-1">All Filters</span>
        </button>
      )}
    </div>
  );
};

FilterSelectors.propTypes = {
  filterValues: PropTypes.shape({
    requestId: PropTypes.string,
    clientName: PropTypes.string,
    status: PropTypes.string,
    contractedPrograms: PropTypes.string,
    contractedProgramName: PropTypes.string,
    primaryHealthInsuranceMemberId: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    requestId: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    clientName: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    status: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    contractedProgram: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  openFilterDrawer: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  setRequestIdFilter: PropTypes.func.isRequired,
  setClientNameFilter: PropTypes.func.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  setContractedProgramFilter: PropTypes.func.isRequired,
  setPrimaryHealthInsuranceMemberIdFilter: PropTypes.func.isRequired,
  authorizationStatus: PropTypes.string.isRequired,
};

export default FilterSelectors;
